import {Routes, Route, useLocation} from "react-router-dom"
import {Box, Grid, Typography, Tooltip, IconButton, Link, Alert} from "@mui/material"
import SnoozeIcon from "@mui/icons-material/Snooze"
import WarningIcon from "@mui/icons-material/Warning"
import React from "react"
import {useNavigate} from "react-router"

import {AdvancedSearchEntryLink} from "../AdvancedSearch/AdvancedSearchEntryLink"

import {Tabs} from "./Tabs"
import {CustomerMenu} from "./CustomerMenu"
import {Overview} from "./Overview"
import {Cases} from "./Cases/Cases"
import {Conversations} from "./Conversations/Conversations"
import {Accounts} from "./Accounts"
import {PaymentPlans} from "./PaymentPlans/PaymentPlans"
import {Notes} from "./Notes"
import {NotesV2} from "./NotesV2"
import {Transactions} from "./Transactions"
import {Activities} from "./Activities"
import {CustomerOffers} from "./CustomerOffers/CustomerOffers"
import {OffersAvailableBadge} from "./CustomerOffers/OffersAvailableBadge"
import {OfferModal} from "./OfferModal"
import IconDiscount from "./IconDiscount.svg"
import {CustomerEligibleOffersModal} from "./CustomerEligibleOffersModal"
import {CollectionHoldsBanner} from "./CollectionHoldsBanner"

import {SystemOfRecordChip} from "@indebted/components/SystemOfRecordChip"
import {useNotification} from "@indebted/components/Notification"
import {api, Customer, SettlementOfferResponse} from "@indebted/api"
import {Search} from "@indebted/components/Search"

function CustomerCard({
	customer,
	refreshCustomerData,
	refreshingCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
	refreshingCustomerData: boolean
}) {
	const location = useLocation()
	const navigate = useNavigate()
	const search = new URLSearchParams(location.search)

	return (
		<>
			<Search
				value={search.get("q")}
				backURL={search.get("back")}
				secondaryAction={<AdvancedSearchEntryLink onClick={() => navigate("/customers?advanced=true")} />}
			/>
			<Box p={3} pt={0}>
				{customer.SystemOfRecord === "Latitude" && (
					<Grid container style={{marginBottom: "16px"}}>
						<Grid size={12}>
							<Alert severity="warning">
								This is a Latitude customer. No action should be taken in Rocket.
								{customer.Latitude && (
									<>
										{" "}
										{customer.Latitude.Link && (
											<Link
												href={customer.Latitude.Link}
												target="_blank"
												rel="noopener noreferrer"
											>
												View in Latitude
											</Link>
										)}
										<br />
										Collection Comms Enabled:{" "}
										{customer.Latitude.CollectionCommsEnabled ? "Yes" : "No"}
										<br />
										Payments Enabled: {customer.Latitude.PaymentsEnabled ? "Yes" : "No"}
									</>
								)}
							</Alert>
						</Grid>
					</Grid>
				)}
				<Grid container alignItems="center">
					<Grid size="auto" style={{display: "flex", alignItems: "center"}}>
						<CustomerStatus customer={customer} />
					</Grid>
					<Grid size="grow" style={{flexGrow: 1}}>
						<Box display="flex" alignItems="center">
							<Typography variant="h5">{customer.FullName}</Typography>
							<SystemOfRecordChip systemOfRecord={customer.SystemOfRecord} />
							{customer.EnableRocketOffersTab && <OffersAvailableBadge />}
						</Box>
					</Grid>
					<CustomerActions customer={customer} />
					{customer.EnableRocketCollectionHolds &&
						(refreshingCustomerData ||
							(customer.CollectionHolds && customer.CollectionHolds.length > 0)) && (
							<Grid size="auto">
								<CollectionHoldsBanner
									customerID={customer.ID}
									holds={customer.CollectionHolds || []}
									refreshing={refreshingCustomerData}
									reload={refreshCustomerData}
								/>
							</Grid>
						)}
					<Grid size="auto">
						<CustomerMenu customer={customer} reload={refreshCustomerData} />
					</Grid>
				</Grid>
				<AccountClosureAlert customer={customer} />
			</Box>

			<Tabs customer={customer} />

			<Routes>
				<Route
					path="/"
					element={<OverviewTab customer={customer} refreshCustomerData={refreshCustomerData} />}
				/>
				<Route
					path="/cases"
					element={<CasesTab customer={customer} refreshCustomerData={refreshCustomerData} />}
				/>
				<Route path="/conversations" element={<ConversationsTab customer={customer} />} />
				<Route
					path="/notes"
					element={<NoteTab customer={customer} refreshCustomerData={refreshCustomerData} />}
				/>
				<Route
					path="/accounts/*"
					element={<AccountsTab customer={customer} refreshCustomerData={refreshCustomerData} />}
				/>
				<Route
					path="/paymentplans"
					element={<PaymentPlansTab customer={customer} refreshCustomerData={refreshCustomerData} />}
				/>
				<Route path="/activities/*" element={<ActivitiesTab customer={customer} />} />
				<Route
					path="/transactions"
					element={<TransactionsTab customer={customer} refreshCustomerData={refreshCustomerData} />}
				/>
				{customer.EnableRocketOffersTab && <Route path="/offers" element={<OffersTab customer={customer} />} />}
			</Routes>
		</>
	)
}

function CustomerStatus({customer}: {customer: Customer}): JSX.Element | null {
	const snoozeMap: Record<string, JSX.Element | null> = {
		true: (
			<Tooltip title={`until ${customer.SnoozedUntil}`} placement="right">
				<SnoozeIcon style={{paddingRight: "0.5rem", fontSize: "2rem"}} />
			</Tooltip>
		),
		false: null,
	}

	const escalationMap: Record<string, JSX.Element | null> = {
		true: (
			<Tooltip title={customer.Escalation} placement="right">
				<WarningIcon style={{paddingRight: "0.5rem", fontSize: "2rem"}} />
			</Tooltip>
		),
		false: null,
	}

	return escalationMap[String(!!customer.Escalation)] || snoozeMap[String(!!customer.CommunicationSnoozed)]
}

function CustomerActions({customer}: {customer: Customer}) {
	const location = useLocation()
	const navigation = useNavigate()
	const [offerData, setOfferData] = React.useState<SettlementOfferResponse | undefined>()
	const {notification} = useNotification()

	function setupDiscountOffer() {
		if (customer.EnableRocketOffersTab) {
			navigation("offers" + location.search)
			return
		}
		api.customer
			.getSettlementOffer(customer.ID)
			.then((response) => {
				setOfferData(response)
			})
			.catch((error: {Message: string}) => {
				notification.error(error.Message)
			})
	}

	if (customer.CustomerPaidCommissions) {
		return null
	}

	return (
		<>
			{offerData ? (
				<OfferModal offerData={offerData} customerID={customer.ID} onClose={() => setOfferData(undefined)} />
			) : null}
			<Grid size="auto">
				<CustomerEligibleOffersModal customerID={customer.ID} />
			</Grid>
			{customer.EnableCreateSettlementOffer && (
				<Grid size="auto">
					<IconButton onClick={setupDiscountOffer} title="Settle account with discount">
						<img src={IconDiscount} width={20} />
					</IconButton>
				</Grid>
			)}
		</>
	)
}

function AccountClosureAlert({customer}: {customer: Customer}) {
	const allAccounts = [...customer.OtherAccounts, ...customer.OutstandingAccounts, ...customer.PaymentPlanAccounts]
	const closedAccounts = allAccounts.filter((acc) => acc.Closed)
	const hasClosedAccounts = closedAccounts.length > 0
	const hasAllAccountsClosed = hasClosedAccounts && allAccounts.length == closedAccounts.length

	return (
		<>
			{hasClosedAccounts && (
				<Grid size="grow" style={{flexGrow: 1, paddingTop: "5px"}}>
					{hasAllAccountsClosed ? (
						<Alert severity="error">Account(s) closed for collection</Alert>
					) : (
						<Alert severity="warning">Customer has both open and closed accounts for collection</Alert>
					)}
				</Grid>
			)}
		</>
	)
}

function OverviewTab({
	customer,
	refreshCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
}) {
	return <Overview customer={customer} reload={refreshCustomerData} />
}

function CasesTab({
	customer,
	refreshCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
}) {
	return <Cases customerID={customer.ID} refreshCustomerData={refreshCustomerData} />
}

function ConversationsTab({customer}: {customer: Customer}) {
	return <Conversations customerID={customer.ID} />
}

function NoteTab({
	customer,
	refreshCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
}) {
	return customer.UseNotesV2 ? (
		<NotesV2 customerID={customer.ID} reload={refreshCustomerData} systemOfRecord={customer.SystemOfRecord} />
	) : (
		<Notes
			customerID={customer.ID}
			notes={customer.Notes}
			reload={refreshCustomerData}
			systemOfRecord={customer.SystemOfRecord}
		/>
	)
}

function AccountsTab({
	customer,
	refreshCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
}) {
	return <Accounts customer={customer} reload={refreshCustomerData} />
}

function TransactionsTab({
	customer,
	refreshCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
}) {
	return <Transactions transactions={customer.Transactions} reload={refreshCustomerData} />
}

function PaymentPlansTab({
	customer,
	refreshCustomerData,
}: {
	customer: Customer
	refreshCustomerData: (delayMs?: number) => void
}) {
	return (
		<PaymentPlans
			activePaymentPlans={customer.ActivePaymentPlans}
			reload={refreshCustomerData}
			customerID={customer.ID}
			totalCustomerBalance={customer.TotalCustomerAvailableBalanceInCents / 100}
		/>
	)
}

function ActivitiesTab({customer}: {customer: Customer}) {
	return <Activities customerID={customer.ID} />
}

function OffersTab({customer}: {customer: Customer}) {
	return (
		<CustomerOffers
			enableCustomerOffersV2={customer.EnableCustomerOffersV2}
			minInstalmentAmount={10}
			balanceInCents={customer.TotalCustomerAvailableBalanceInCents}
		/>
	)
}

export {CustomerCard}
